import React from 'react';
import styled from 'styled-components';

import { useMediaQuery } from '@/components/utils/useQueryMedia';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

import {
  CONFIGURATOR_MOBILE_MEDIA_QUERY,
  CONFIGURATOR_STEPS_WITH_CASINO_NAME,
  CONFIGURATOR_STEPS_WITH_PREVIEW,
} from './constants';
import { useBreakpoints } from './hooks/useBreakpoints';
import { useConfigFetch } from './hooks/useConfigFetch';
import { useDebugging } from './hooks/useDebugging';
import { useEngagementStep } from './hooks/useEngagementStep';
import { useFullscreen } from './hooks/useFullscreen';
import { useIframe } from './hooks/useIframe';
import { useLiveStep } from './hooks/useLiveStep';
import { useSteps } from './hooks/useSteps';
import { useThemeStep } from './hooks/useThemeStep';
import { useTrafficStep } from './hooks/useTrafficStep';
import { ConfiguratorProps } from './types';

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const SparkConfigurator: React.FC<ConfiguratorProps> = (componentProps) => {
  let props = componentProps;
  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const isMobile = useMediaQuery(CONFIGURATOR_MOBILE_MEDIA_QUERY);

  const {
    configDesignOptions,
    configArtOptions,
    configEngagementPackages,
    configTrafficDetails,
    configTrafficPackages,
    configLoading,
  } = useConfigFetch();

  const { step, steps } = useSteps();
  const { onEnterFullscreen, onExitFullscreen, showEnterFullscreen, showExitFullscreen } = useFullscreen();
  const { breakpoint, breakpoints } = useBreakpoints();
  const {
    themeCategory,
    themeCategories,
    themeDesignOption,
    themeDesignOptions,
    themeColorsOption,
    themeColorsOptions,
    themeArtOption,
    themeArtOptions,
    casinoName,
    casinoNameError,
    onCasinoNameChange,
  } = useThemeStep(configDesignOptions, configArtOptions);
  const { engagementPackage, engagementPackages, engagementLayout } = useEngagementStep(configEngagementPackages);
  const { trafficDetails, trafficPackage, trafficPackages, trafficLayout } = useTrafficStep(configTrafficDetails, configTrafficPackages);
  const {
    liveDetails,
    liveLayout,
    liveOrderNumber,
    accountEmail,
    accountEmailError,
    onAccountEmailChange,
    confirmEmail,
    confirmEmailError,
    onConfirmEmailChange,
    termsAccepted,
    onTermsChange,
  } = useLiveStep();
  const { iframeLoading, iframeUrlParams } = useIframe(isMobile, themeDesignOption, themeColorsOption);
  const { debuggingMessage } = useDebugging(configLoading, iframeLoading);

  const contextValue = React.useMemo(
    () => ({
      configLoading,
      showCasinoName: CONFIGURATOR_STEPS_WITH_CASINO_NAME.includes(step.id),
      showPreviewContainer: CONFIGURATOR_STEPS_WITH_PREVIEW.includes(step.id),
      step,
      steps,
      themeCategory,
      themeCategories,
      themeDesignOption,
      themeDesignOptions,
      themeColorsOption,
      themeColorsOptions,
      themeArtOption,
      themeArtOptions,
      casinoName,
      casinoNameError,
      onCasinoNameChange,
      engagementPackage,
      engagementPackages,
      engagementLayout,
      trafficDetails,
      trafficPackage,
      trafficPackages,
      trafficLayout,
      liveDetails,
      liveLayout,
      liveOrderNumber,
      accountEmail,
      accountEmailError,
      onAccountEmailChange,
      confirmEmail,
      confirmEmailError,
      onConfirmEmailChange,
      termsAccepted,
      onTermsChange,
      onEnterFullscreen,
      onExitFullscreen,
      showEnterFullscreen,
      showExitFullscreen,
      breakpoint,
      breakpoints,
      iframeLoading,
      iframeUrlParams,
      isMobile: isMobile,
      isDesktop: !isMobile,
      debuggingMessage,
    }),
    [
      props,
      configLoading,
      step,
      steps,
      themeCategory,
      themeCategories,
      themeDesignOption,
      themeDesignOptions,
      themeColorsOption,
      themeColorsOptions,
      themeArtOption,
      themeArtOptions,
      casinoName,
      casinoNameError,
      onCasinoNameChange,
      engagementPackage,
      engagementPackages,
      engagementLayout,
      trafficDetails,
      trafficPackage,
      trafficPackages,
      trafficLayout,
      liveDetails,
      liveLayout,
      liveOrderNumber,
      accountEmail,
      accountEmailError,
      onAccountEmailChange,
      confirmEmail,
      confirmEmailError,
      onConfirmEmailChange,
      termsAccepted,
      onTermsChange,
      onEnterFullscreen,
      onExitFullscreen,
      showEnterFullscreen,
      showExitFullscreen,
      breakpoint,
      breakpoints,
      iframeLoading,
      iframeUrlParams,
      isMobile,
      debuggingMessage,
    ],
  );

  console.log('SparkConfigurator[contextValue]', contextValue, props);

  return (
    <ModuleElementDiv className={componentProps.className} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default SparkConfigurator;
